import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import {
  IDENTITY_PROVIDERS,
  MEMBERSHIP_ROLES,
  MEMBERSHIP_STATUSES,
  ORG_KINDS,
  ORG_STATUSES,
  PLATFORM_ROLES,
  USER_STATUSES,
} from '../roles'
import { idSchema, isIsoDate, reasonSchema, revisionSchema, text } from '../validators'
import { emailSchema, INVITE_STATUSES } from './auth'

/**
 * Admin panel contracts (M-01, M-04; P-ADM-005/007/008/009/012/013).
 * All routes live under `/api/v1/admin/*` and need a platform user, except
 * invites, which an investor_admin may also manage for their own org (P-ID-010).
 */

const optionalText = (max: number) => z.string().trim().max(max).optional()

// ---------------------------------------------------------------------------
// Orgs (P-ADM-005)
// ---------------------------------------------------------------------------

export const ORG_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const orgSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(ORG_SLUG_PATTERN, { message: 'Use lower-case letters, digits and single hyphens.' })

export const adminOrgSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  kind: z.enum(ORG_KINDS),
  status: z.enum(ORG_STATUSES),
  suspended_at: z.string().nullable(),
  suspend_reason: z.string().nullable(),
  member_count: z.number().int(),
  pending_invites: z.number().int(),
  revision: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type AdminOrg = z.infer<typeof adminOrgSchema>

export const orgListQuerySchema = pageQuerySchema.extend({
  kind: z.enum(ORG_KINDS).optional(),
  status: z.enum(ORG_STATUSES).optional(),
  q: optionalText(120),
})

export const orgCreateSchema = z.strictObject({
  name: text(2, 120),
  slug: orgSlugSchema,
})
export type OrgCreate = z.infer<typeof orgCreateSchema>

export const orgUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    name: text(2, 120).optional(),
    slug: orgSlugSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.slug !== undefined, {
    message: 'Change the name or the slug.',
  })
export type OrgUpdate = z.infer<typeof orgUpdateSchema>

export const orgSuspendSchema = z.strictObject({ revision: revisionSchema, reason: reasonSchema })
export type OrgSuspend = z.infer<typeof orgSuspendSchema>

export const revisionOnlySchema = z.strictObject({ revision: revisionSchema })

// ---------------------------------------------------------------------------
// Users (P-ADM-007/008)
// ---------------------------------------------------------------------------

export const adminMembershipSchema = z.object({
  org_id: z.string(),
  org_name: z.string(),
  org_kind: z.enum(ORG_KINDS),
  org_status: z.enum(ORG_STATUSES),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(MEMBERSHIP_STATUSES),
})
export type AdminMembership = z.infer<typeof adminMembershipSchema>

export const adminInviteSchema = z.object({
  id: z.string(),
  email: z.string(),
  org_id: z.string(),
  org_name: z.string(),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(INVITE_STATUSES),
  invited_by: z.string(),
  invited_by_name: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string(),
  accepted_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  resent_at: z.string().nullable(),
  resend_count: z.number().int(),
})
export type AdminInvite = z.infer<typeof adminInviteSchema>

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  status: z.enum(USER_STATUSES),
  platform_role: z.enum(PLATFORM_ROLES).nullable(),
  last_sign_in_at: z.string().nullable(),
  mfa_enrolled: z.boolean(),
  memberships: z.array(adminMembershipSchema),
  pending_invites: z.array(adminInviteSchema),
  /** Active, non-impersonated access exists: the operator may "View as" this user. */
  can_impersonate: z.boolean(),
  revision: z.number().int(),
  created_at: z.string(),
})
export type AdminUser = z.infer<typeof adminUserSchema>

export const adminIdentitySchema = z.object({
  id: z.string(),
  provider: z.enum(IDENTITY_PROVIDERS),
  email: z.string(),
  email_verified: z.boolean(),
  tid: z.string().nullable(),
  linked_at: z.string(),
  last_used_at: z.string().nullable(),
})
export type AdminIdentity = z.infer<typeof adminIdentitySchema>

export const adminUserDetailSchema = adminUserSchema.extend({
  identities: z.array(adminIdentitySchema),
  active_sessions: z.number().int(),
  deactivated_at: z.string().nullable(),
  deactivation_reason: z.string().nullable(),
  mfa_enrolled_at: z.string().nullable(),
})
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>

export const userListQuerySchema = pageQuerySchema.extend({
  org_id: idSchema.optional(),
  status: z.enum(USER_STATUSES).optional(),
  q: optionalText(120),
})

/** Set one investor org role, or remove the user from the org with `role: null`. */
export const userRoleChangeSchema = z.strictObject({
  org_id: idSchema,
  role: z.enum(['investor', 'investor_admin']).nullable(),
})
export type UserRoleChange = z.infer<typeof userRoleChangeSchema>

export const userDeactivateSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
})
export type UserDeactivate = z.infer<typeof userDeactivateSchema>

export const sessionsRevokedSchema = z.object({ ok: z.literal(true), revoked: z.number().int() })

// ---------------------------------------------------------------------------
// Invites (P-ID-010, P-ADM-007)
// ---------------------------------------------------------------------------

export const inviteCreateSchema = z.strictObject({
  email: emailSchema,
  org_id: idSchema,
  role: z.enum(MEMBERSHIP_ROLES),
  display_name: text(2, 120).optional(),
})
export type InviteCreate = z.infer<typeof inviteCreateSchema>

export const inviteCreatedSchema = z.object({
  /** `invited`: a pending invite was emailed. `granted`: an active user got the role at once. */
  outcome: z.enum(['invited', 'granted']),
  invite: adminInviteSchema,
  user_id: z.string(),
  email_sent: z.boolean(),
})
export type InviteCreated = z.infer<typeof inviteCreatedSchema>

export const inviteListQuerySchema = pageQuerySchema.extend({
  org_id: idSchema.optional(),
  status: z.enum(INVITE_STATUSES).optional(),
})

// ---------------------------------------------------------------------------
// Staff (P-ADM-008) and platform users (P-ADM-009)
// ---------------------------------------------------------------------------

export const staffMemberSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  status: z.enum(USER_STATUSES),
  platform_role: z.enum(PLATFORM_ROLES).nullable(),
  manager: z.boolean(),
  reviewer: z.boolean(),
  last_sign_in_at: z.string().nullable(),
  mfa_enrolled: z.boolean(),
})
export type StaffMember = z.infer<typeof staffMemberSchema>

export const staffRolesSchema = z.strictObject({ manager: z.boolean(), reviewer: z.boolean() })
export type StaffRoles = z.infer<typeof staffRolesSchema>

export const platformUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  status: z.enum(USER_STATUSES),
  platform_role: z.enum(PLATFORM_ROLES),
  /** In `OPERATOR_EMAILS`: re-granted at every sign-in, so it can't be revoked here. */
  configured_operator: z.boolean(),
  last_sign_in_at: z.string().nullable(),
  mfa_enrolled: z.boolean(),
})
export type PlatformUser = z.infer<typeof platformUserSchema>

export const operatorGrantSchema = z.strictObject({ operator: z.boolean() })
export type OperatorGrant = z.infer<typeof operatorGrantSchema>

// ---------------------------------------------------------------------------
// Impersonation (P-ADM-012)
// ---------------------------------------------------------------------------

export const impersonateSchema = z.strictObject({
  user_id: idSchema,
  reason: text(8, 500),
})
export type ImpersonateRequest = z.infer<typeof impersonateSchema>

export const impersonationStartedSchema = z.object({
  ok: z.literal(true),
  user_id: z.string(),
  expires_at: z.string(),
  /** Where the SPA should go: the target's first area. */
  redirect: z.string(),
})
export type ImpersonationStarted = z.infer<typeof impersonationStartedSchema>

export const impersonationEndedSchema = z.object({ ok: z.literal(true), redirect: z.string() })
export type ImpersonationEnded = z.infer<typeof impersonationEndedSchema>

// ---------------------------------------------------------------------------
// Platform audit (P-ADM-013)
// ---------------------------------------------------------------------------

const dateParam = z.string().refine(isIsoDate, { message: 'Use YYYY-MM-DD dates.' }).optional()

export const auditListQuerySchema = pageQuerySchema
  .extend({
    /** Action prefix, e.g. `auth.` or `impersonation.start`. */
    action: z
      .string()
      .trim()
      .max(80)
      .regex(/^[a-z_.]*$/)
      .optional(),
    actor_id: idSchema.optional(),
    org_id: idSchema.optional(),
    from: dateParam,
    to: dateParam,
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, {
    path: ['from'],
    message: 'Start date must not follow end date.',
  })

export const auditEventSchema = z.object({
  chain: z.string(),
  seq: z.number().int(),
  ts: z.string(),
  actor_id: z.string().nullable(),
  actor_name: z.string().nullable(),
  impersonated_by: z.string().nullable(),
  request_id: z.string().nullable(),
  action: z.string(),
  kind: z.string(),
  record_id: z.string().nullable(),
  org_id: z.string().nullable(),
  details: z.unknown(),
  hash: z.string(),
})
export type PlatformAuditEventDto = z.infer<typeof auditEventSchema>

export const auditVerifySchema = z.object({
  chain: z.string(),
  ok: z.boolean(),
  count: z.number().int(),
  head_hash: z.string().nullable(),
  failure: z.string().nullable(),
  failed_seq: z.number().int().nullable(),
})
export type AuditVerify = z.infer<typeof auditVerifySchema>
